import type { LabelDto } from '@keep/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { noteKeys } from './notes-cache';

export const labelsKey = ['labels'] as const;

export function useLabels() {
  return useQuery({
    queryKey: labelsKey,
    queryFn: () => api.get<LabelDto[]>('/labels'),
  });
}

const byName = (a: LabelDto, b: LabelDto) =>
  a.name.localeCompare(b.name, 'fr', { sensitivity: 'base' });

export function useCreateLabel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => api.post<LabelDto>('/labels', { name }),
    onSuccess: (label) =>
      queryClient.setQueryData<LabelDto[]>(labelsKey, (labels = []) =>
        [...labels, label].sort(byName),
      ),
  });
}

export function useRenameLabel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      api.patch<LabelDto>(`/labels/${id}`, { name }),
    onSuccess: (label) =>
      queryClient.setQueryData<LabelDto[]>(labelsKey, (labels = []) =>
        labels.map((l) => (l.id === label.id ? label : l)).sort(byName),
      ),
  });
}

export function useDeleteLabel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/labels/${id}`),
    onSuccess: (_result, id) => {
      queryClient.setQueryData<LabelDto[]>(labelsKey, (labels = []) =>
        labels.filter((l) => l.id !== id),
      );
      void queryClient.invalidateQueries({ queryKey: noteKeys.all });
    },
  });
}
